import type { ReactNode } from 'react';
import type { ProposalTestimonialV1 } from '../../../worker/render/types';
import { Field, SectionCard, TextArea, TextInput } from './fields';
import OmitEmptyCustom from './OmitEmptyCustom';
import type { SectionProps } from './types';

export function FaqSection({ doc, onChange, disabled }: SectionProps) {
  return (
    <SectionCard title="FAQ">
      <OmitEmptyCustom
        label="Questions"
        defaultsLabel="the five built-in questions"
        itemLabel="question"
        value={doc.faq}
        onChange={(faq) => onChange((d) => ({ ...d, faq }))}
        makeBlank={() => ({ q: '', a: '' })}
        disabled={disabled}
      >
        {(item, update) => (
          <>
            <TextInput placeholder="Question" value={item.q} onChange={(e) => update({ q: e.target.value })} disabled={disabled} />
            <TextArea rows={2} placeholder="Answer" value={item.a} onChange={(e) => update({ a: e.target.value })} disabled={disabled} />
          </>
        )}
      </OmitEmptyCustom>
    </SectionCard>
  );
}

export function TestimonialsSection({ doc, onChange, disabled }: SectionProps) {
  const proof = doc.investment_proof;
  return (
    <SectionCard title="Testimonials">
      <OmitEmptyCustom<ProposalTestimonialV1>
        label="Client quotes"
        defaultsLabel="the default pair"
        itemLabel="testimonial"
        value={doc.testimonials}
        onChange={(testimonials) => onChange((d) => ({ ...d, testimonials }))}
        makeBlank={() => ({ quote: '', name: '' })}
        disabled={disabled}
      >
        {(item, update) => (
          <>
            <TextArea rows={2} placeholder="Quote" value={item.quote} onChange={(e) => update({ quote: e.target.value })} disabled={disabled} />
            <TextInput placeholder="Name" value={item.name} onChange={(e) => update({ name: e.target.value })} disabled={disabled} />
            <TextInput
              placeholder="Role (optional)"
              value={item.role ?? ''}
              onChange={(e) => update({ role: e.target.value })}
              disabled={disabled}
            />
          </>
        )}
      </OmitEmptyCustom>

      <div className="space-y-2.5 border-t border-line pt-5">
        <div>
          <span className="text-sm font-medium text-ink">Proof card</span>
          <p className="mt-1 text-xs text-ink-faint">
            Shown under the pricing grid. Leave blank to fall back to the first testimonial above; if there's
            nothing to show, no card renders.
          </p>
        </div>
        <Field label="Quote">
          <TextArea
            rows={2}
            value={proof?.quote ?? ''}
            onChange={(e) => onChange((d) => ({ ...d, investment_proof: withProofPatch(d.investment_proof, { quote: e.target.value }) }))}
            disabled={disabled}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name">
            <TextInput
              value={proof?.name ?? ''}
              onChange={(e) => onChange((d) => ({ ...d, investment_proof: withProofPatch(d.investment_proof, { name: e.target.value }) }))}
              disabled={disabled}
            />
          </Field>
          <Field label="Role (optional)">
            <TextInput
              value={proof?.role ?? ''}
              onChange={(e) => onChange((d) => ({ ...d, investment_proof: withProofPatch(d.investment_proof, { role: e.target.value }) }))}
              disabled={disabled}
            />
          </Field>
        </div>
        <Field label="Result (optional)" hint="e.g. '3x more enquiries'.">
          <TextInput
            value={proof?.result ?? ''}
            onChange={(e) => onChange((d) => ({ ...d, investment_proof: withProofPatch(d.investment_proof, { result: e.target.value }) }))}
            disabled={disabled}
          />
        </Field>
      </div>
    </SectionCard>
  );
}

function withProofPatch(
  current: NonNullable<SectionProps['doc']['investment_proof']> | undefined,
  patch: Partial<NonNullable<SectionProps['doc']['investment_proof']>>,
) {
  const next = { quote: current?.quote ?? '', name: current?.name ?? '', role: current?.role, result: current?.result, ...patch };
  // An entirely empty proof card is the same as omitting it.
  if (!next.quote && !next.name && !next.role && !next.result) return undefined;
  return next;
}

export function TimelineSection({ doc, onChange, disabled }: SectionProps) {
  return (
    <SectionCard title="Timeline">
      <OmitEmptyCustom
        label="Stages"
        defaultsLabel="the six default stages"
        hint="Relative durations only, e.g. 'Week 1' or '~2 weeks after the shoot'. Never calendar dates."
        itemLabel="stage"
        value={doc.timeline}
        onChange={(timeline) => onChange((d) => ({ ...d, timeline }))}
        makeBlank={() => ({ label: '', duration: '' })}
        disabled={disabled}
        allowHidden={false}
      >
        {(item, update) => (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextInput placeholder="Label" value={item.label} onChange={(e) => update({ label: e.target.value })} disabled={disabled} />
            <TextInput
              placeholder="Duration, e.g. Week 1"
              value={item.duration}
              onChange={(e) => update({ duration: e.target.value })}
              disabled={disabled}
            />
          </div>
        )}
      </OmitEmptyCustom>
    </SectionCard>
  );
}

type VideoMode = 'default' | 'custom' | 'none';

function videoModeOf(value: SectionProps['doc']['reference_video']): VideoMode {
  if (value === undefined) return 'default';
  return value === null ? 'none' : 'custom';
}

export function ReferenceVideoSection({ doc, onChange, disabled }: SectionProps) {
  const mode = videoModeOf(doc.reference_video);
  const video = doc.reference_video;

  return (
    <SectionCard title="Reference video" hint="One video only. Never a reference video and a showreel together.">
      <div className="flex flex-wrap gap-2">
        <ModeButton
          active={mode === 'default'}
          onClick={() => onChange((d) => ({ ...d, reference_video: undefined }))}
          disabled={disabled}
        >
          Default reel
        </ModeButton>
        <ModeButton
          active={mode === 'custom'}
          onClick={() =>
            onChange((d) => ({
              ...d,
              reference_video: d.reference_video ?? { url: '', why: '' },
            }))
          }
          disabled={disabled}
        >
          Custom video
        </ModeButton>
        <ModeButton active={mode === 'none'} onClick={() => onChange((d) => ({ ...d, reference_video: null }))} disabled={disabled}>
          None
        </ModeButton>
      </div>

      {mode === 'default' && <p className="text-xs text-ink-faint">Henry's default reel plays here, with generic framing copy.</p>}
      {mode === 'none' && <p className="text-xs text-ink-faint">Section is hidden on the live proposal.</p>}

      {mode === 'custom' && video && (
        <div className="space-y-3.5 rounded-xl border border-line bg-paper p-4">
          <Field label="Video URL" hint="YouTube watch, short, or embed link. Must start with https://.">
            <TextInput
              value={video.url}
              onChange={(e) => onChange((d) => ({ ...d, reference_video: { ...withVideo(d.reference_video), url: e.target.value } }))}
              disabled={disabled}
            />
          </Field>
          <Field label="Title (optional)">
            <TextInput
              value={video.title ?? ''}
              onChange={(e) => onChange((d) => ({ ...d, reference_video: { ...withVideo(d.reference_video), title: e.target.value } }))}
              disabled={disabled}
            />
          </Field>
          <Field label="Why this video" hint="Why it's the benchmark for this project.">
            <TextArea
              rows={2}
              value={video.why}
              onChange={(e) => onChange((d) => ({ ...d, reference_video: { ...withVideo(d.reference_video), why: e.target.value } }))}
              disabled={disabled}
            />
          </Field>
        </div>
      )}
    </SectionCard>
  );
}

function withVideo(value: SectionProps['doc']['reference_video']) {
  return value && typeof value === 'object' ? value : { url: '', why: '' };
}

export function LinksSection({ doc, onChange, disabled }: SectionProps) {
  return (
    <SectionCard title="Links">
      <Field label="Scheduling link" hint="Enables the 'Book your kickoff call' step after signing. Must start with https://.">
        <TextInput
          value={doc.scheduling_url ?? ''}
          onChange={(e) => onChange((d) => ({ ...d, scheduling_url: e.target.value || null }))}
          disabled={disabled}
        />
      </Field>
      <Field label="Assets request link" hint="Enables the 'Send us your brand assets' step after signing. Must start with https://.">
        <TextInput
          value={doc.assets_request_url ?? ''}
          onChange={(e) => onChange((d) => ({ ...d, assets_request_url: e.target.value || null }))}
          disabled={disabled}
        />
      </Field>
    </SectionCard>
  );
}

function ModeButton({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        'rounded-full border px-3.5 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ' +
        (active ? 'border-accent bg-accent-tint text-accent-deep' : 'border-line text-ink-soft hover:border-ink-faint')
      }
    >
      {children}
    </button>
  );
}
